import type { FieldMeta } from '../../../engine/meta/types';
import type { IconName } from '../../../engine/design/icons';

// The designer's palette + the bridge from the server's node JSON Schemas
// (GET /v1/workflows/node-schemas, generated from the zod schemas in
// src/modules/servicedesk/workflow/schema.ts) to FieldMeta so AutoForm renders each node's
// property inspector. The server stays the single source of truth for node shapes; the
// client only knows how to present them and a few structural rules for inline validation.

export type NodeType = 'trigger' | 'condition' | 'set_field' | 'assign' | 'change_status' | 'add_action' | 'send_email' | 'notify' | 'wait_for_condition' | 'approval' | 'branch' | 'end';

export interface PaletteEntry {
  type: NodeType;
  label: string;
  icon: IconName;
  hint: string;
  /** Edge labels the node routes on (undefined = a single exit). */
  outcomes?: string[];
  defaultConfig: Record<string, unknown>;
}

export const PALETTE: PaletteEntry[] = [
  { type: 'condition', label: 'Condition', icon: 'git-branch', hint: 'True / false on the ticket', outcomes: ['true', 'false'], defaultConfig: { conditions: { any: [{ all: [{ field: 'priority.key', op: 'eq', value: 'p1' }] }] } } },
  { type: 'branch', label: 'Branch', icon: 'layers', hint: 'First matching branch, else', outcomes: ['else'], defaultConfig: { branches: [{ key: 'a', when: { any: [{ all: [{ field: 'ticket_type.key', op: 'eq', value: 'incident' }] }] } }] } },
  { type: 'set_field', label: 'Set field', icon: 'pencil', hint: 'Write a ticket field', defaultConfig: { field: 'queue_id', value: '' } },
  { type: 'assign', label: 'Assign', icon: 'user', hint: 'Technician / queue / round robin', defaultConfig: { mode: 'round_robin' } },
  { type: 'change_status', label: 'Change status', icon: 'circle-dot', hint: 'Move to a status', defaultConfig: { status_key: 'in_progress' } },
  { type: 'add_action', label: 'Add action', icon: 'message-square', hint: 'Note or reply with an outcome', defaultConfig: { outcome_key: 'note', body: '', is_public: false } },
  { type: 'send_email', label: 'Send email', icon: 'mail', hint: 'Template to contact / watchers', defaultConfig: { template_key: 'ticket.public_reply', to: 'contact' } },
  { type: 'notify', label: 'Notify', icon: 'bell', hint: 'In-app notification', defaultConfig: { to: 'assignee', title: 'Ticket {{ticket.ref}} needs attention' } },
  { type: 'wait_for_condition', label: 'Wait for condition', icon: 'clock', hint: 'Suspend until true, or time out', outcomes: ['done', 'timeout'], defaultConfig: { conditions: { any: [{ all: [{ field: 'status.category', op: 'neq', value: 'pending' }] }] }, timeout_minutes: 2880, use_calendar: true } },
  { type: 'approval', label: 'Approval', icon: 'shield-check', hint: 'Run an approval process', outcomes: ['approved', 'rejected', 'timed_out'], defaultConfig: { process_key: 'service_request_approval' } },
  { type: 'end', label: 'End', icon: 'check-circle', hint: 'Finish the run', defaultConfig: {} },
];

export const NODE_ICON: Record<NodeType, IconName> = { trigger: 'zap', condition: 'git-branch', branch: 'layers', set_field: 'pencil', assign: 'user', change_status: 'circle-dot', add_action: 'message-square', send_email: 'mail', notify: 'bell', wait_for_condition: 'clock', approval: 'shield-check', end: 'check-circle' };

export function outcomesOfType(type: NodeType, config: Record<string, unknown>): string[] | undefined {
  if (type === 'branch') return [...((config['branches'] as Array<{ key: string }> | undefined) ?? []).map((b) => b.key), 'else'];
  return PALETTE.find((p) => p.type === type)?.outcomes;
}

interface JsonSchema {
  type?: string | string[];
  properties?: Record<string, JsonSchema & { title?: string; description?: string; enum?: string[]; default?: unknown; format?: string; anyOf?: JsonSchema[] }>;
  required?: string[];
  enum?: string[];
  anyOf?: JsonSchema[];
  items?: JsonSchema;
}

const base = (name: string): FieldMeta => ({ name, label: name.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()), type: 'text', required: false, readOnly: false, nullable: true, filterable: false, sortable: false, searchable: false, inCreate: true, inUpdate: true, inList: false, inDetail: true });

/** JSON Schema (one node's config) → FieldMeta[] for AutoForm. */
export function schemaToFields(schema: JsonSchema | undefined): FieldMeta[] {
  if (!schema?.properties) return [];
  const required = new Set(schema.required ?? []);
  return Object.entries(schema.properties).map(([name, p]) => {
    const f = base(name);
    f.required = required.has(name);
    f.nullable = !required.has(name);
    const t = Array.isArray(p.type) ? p.type[0] : p.type;
    const enumValues = p.enum ?? p.anyOf?.flatMap((a) => a.enum ?? []);
    if (enumValues && enumValues.length > 0) {
      f.type = 'enum';
      f.enum = enumValues.map((v) => ({ value: v, label: v }));
    } else if (t === 'boolean') f.type = 'boolean';
    else if (t === 'integer' || t === 'number') f.type = 'integer';
    else if (t === 'array' || t === 'object' || (!t && !p.format)) f.type = 'json';
    else if (name === 'body') f.type = 'textarea';
    else if (p.format === 'uuid') f.type = 'text';
    if (p.description) f.help = p.description;
    if (p.default !== undefined) f.default = p.default;
    return f;
  });
}

export interface GraphNode { id: string; type: NodeType; name: string; position?: { x: number; y: number }; config: Record<string, unknown> }
export interface GraphEdge { id: string; from: string; to: string; label?: string }
export interface Definition { version: 1; entity: 'ticket'; settings?: { max_iterations?: number; max_automation_depth?: number }; trigger: { type: 'event' | 'manual'; event?: string; conditions?: unknown }; nodes: GraphNode[]; edges: GraphEdge[] }

export interface LocalIssue { nodeId?: string; edgeId?: string; message: string }

/** Inline structural checks (the server's validate endpoint is authoritative). */
export function validateLocally(def: Definition): LocalIssue[] {
  const issues: LocalIssue[] = [];
  const ids = new Set(def.nodes.map((n) => n.id));
  const triggers = def.nodes.filter((n) => n.type === 'trigger');
  if (triggers.length !== 1) issues.push({ message: `exactly one trigger is required (found ${triggers.length})` });
  if (!def.nodes.some((n) => n.type === 'end')) issues.push({ message: 'add an end node' });
  const outs = new Map<string, GraphEdge[]>();
  const ins = new Set<string>();
  for (const e of def.edges) {
    if (!ids.has(e.from) || !ids.has(e.to)) issues.push({ edgeId: e.id, message: 'edge points at a missing node' });
    (outs.get(e.from) ?? outs.set(e.from, []).get(e.from)!).push(e);
    ins.add(e.to);
  }
  for (const n of def.nodes) {
    const exits = outs.get(n.id) ?? [];
    const labels = outcomesOfType(n.type, n.config);
    if (n.type !== 'end' && exits.length === 0) issues.push({ nodeId: n.id, message: `"${n.name}" has no outgoing edge` });
    if (n.type !== 'trigger' && !ins.has(n.id)) issues.push({ nodeId: n.id, message: `"${n.name}" is unreachable` });
    if (labels) {
      for (const e of exits) if (!e.label || !labels.includes(e.label)) issues.push({ edgeId: e.id, nodeId: n.id, message: `edge from "${n.name}" needs a label: ${labels.join(' / ')}` });
    } else if (exits.length > 1) issues.push({ nodeId: n.id, message: `"${n.name}" may only have one outgoing edge` });
    if (!n.name.trim()) issues.push({ nodeId: n.id, message: 'a node needs a name' });
  }
  return issues;
}

export function emptyDefinition(event = 'ticket.created'): Definition {
  return {
    version: 1,
    entity: 'ticket',
    settings: { max_iterations: 10, max_automation_depth: 3 },
    trigger: { type: 'event', event },
    nodes: [
      { id: 'n_trigger', type: 'trigger', name: 'Trigger', position: { x: 0, y: 0 }, config: {} },
      { id: 'n_end', type: 'end', name: 'End', position: { x: 520, y: 0 }, config: {} },
    ],
    edges: [{ id: 'e_start', from: 'n_trigger', to: 'n_end' }],
  };
}
